
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from 'luxon';
import { startCase, lowerCase, pull } from "lodash";
import { de } from 'date-fns/locale';
import * as actions from "../../../store/actions";
import api from '../../../store/api'
import { toggleGlobalLoader, addErrorAlert, addLoadingAlert, removeLoadingAlert } from "../../../store/calls/alertLoadingDispatch";
import Input from "../../theme/forms/Input";
import AdminWrapper from "../../theme/wrappers/AdminWrapper";
import PaginatedTable from "../../theme/tables/PaginatedTable";
import BaseTable from "../../theme/tables/BaseTable";
import formatters from "../../../utils/forms/formatters";
import CFLineChart from "../../theme/charts/LineChart";
import ProjectCard from "../../subcomponents/dashboard/ProjectCard";
import { getAvailableImprovements } from '../../../store/calls/indicativeOptionData'
import getSubdomainString from "../../../utils/getSubdomainString";


const pullProjectData = (start_date, end_date, subdomain, searchTerm) => {
    return api.get('/dashboard-projects/', { start_date, end_date, subdomain, searchTerm })
        .then(res => res.data)
        .catch(err => addErrorAlert("Initial Data Load Failed"))
}

const pullUserData = (start_date, end_date, subdomain) => {
    return api.get('/dashboard-users/', { start_date, end_date, subdomain })
        .then(res => res.data)
        .catch(err => addErrorAlert("User Data Load Failed"))
}

const pullSiteStatsData = (start_date, end_date, subdomain) => {
    return api.get('/dashboard-stats/', { start_date, end_date, subdomain })
        .then(res => res.data)
        .catch(err => addErrorAlert("Stats Data Load Failed"))
}

const pullSiteTimeseriesData = (start_date, end_date, subdomain) => {
    return api.get('/dashboard-timeseries-stats/', { start_date, end_date, subdomain })
        .then(res => res.data)
        .catch(err => addErrorAlert("Timeseries Data Load Failed"))
}

const pullSubdomainStatsData = (start_date, end_date) => {
    return api.get('/dashboard-subdomain-stats/', { start_date, end_date })
        .then(res => res.data)
        .catch(err => addErrorAlert("Subdomain Stats Data Load Failed"))
}

const pullSubdomainList = () => {
    api.get('/subdomain-configs/')
        .then(res => res.data)
        .catch(res => res.data)
}


const pullAllSubdomainData = (start_date, end_date) => {
    return Promise.all([
        pullSubdomainList(),
        pullSubdomainStatsData(start_date, end_date)
    ])
        .then(res => {
            let [subdomainList, subdomainStats] = res;
            return subdomainList.map(subdomain => ({
                ...subdomain,
                stats: subdomainStats.find(d => subdomain.subdomain === d.subdomain)
            }))
        })
        .catch(err => addErrorAlert("Subdomain Data Load Failed"))

}




function TopUserTable({ projects, users, dashboardStats }) {

    let userTableData = users
        .map(user => ({
            ...user,
            is_new_user: user.is_new_user ? 1 : 0,
            _html_is_new_user: user.is_new_user ? <i className="fas fa-check-double has-text-success" /> : <span />,
            first_name: startCase(user.first_name),
            last_name: startCase(user.last_name),
            project_estimated_total_amount: formatters.USDCurrency.formatter(
                user.project_estimated_total_amount ? user.project_estimated_total_amount : 0
            ),
        }))
        .sort((a, b) => a.project_count > b.project_count ? -1 : 1)

    return (
        <div className="has-background-white p-4">
            <h3 className="is-size-3 mb-4">Active Users</h3>
            <BaseTable
                className="is-fullwidth is-hoverable"
                data={userTableData}
                columns={[
                    'is_new_user',
                    'first_name', 'last_name',
                    'project_count', 'project_estimated_count',
                    'project_estimated_total_amount',
                    'address_search_count',
                    'username', 'email',
                    'company', 'role',]}
                showSortBar
            />
        </div>
    )
}


function TimeseriesChart({ data=[] }) {

    let [dayInterval, setDayInterval] = useState(1);
    let [chartData, setChartData] = useState([]);

    useEffect(() => {
        setChartData(groupDataByDays(data, dayInterval))
    }, [data, dayInterval])

    function groupDataByDays(data, n) {
        const result = [];

        for (let i = 0; i < data.length; i += n) {
            // stop execution so i can debug
            // debugger;

            const group = {
                date: data[i].date,
                total_projects: 0,
                total_applications: 0
            };

            for (let j = i; j < i + n && j < data.length; j++) {
                group.total_projects += data[j].total_projects;
                group.total_applications += data[j].total_applications;
            }

            result.push(group);
        }


        return result;
    }




    return (
        <div className="p-4 has-background-white">
            <div className="my-4 is-flex is-justify-content-space-between">
                <h3 className="is-size-3">
                    Daily Totals
                </h3>
                <Input
                    type="select"
                    label="Interval"
                    value={dayInterval}
                    onChange={val => setDayInterval(+val)}
                    options={[
                        { display: "Day", value: 1},
                        { display: "Week", value: 7 },
                        { display: "Month", value: 30 },
                    ]}
                />
            </div>
            <div style={{ height: '30vw', minHeight: '350px', maxHeight: '700px', width: '100%' }}>
                <CFLineChart
                    data={chartData}
                    config={{
                        lines: [
                            { dataKey: "total_projects", strokeWidth: 4, stroke: "#8884d8" },
                            { dataKey: "total_applications", strokeWidth: 4, stroke: "#82ca9d" }
                        ],
                        xAxis: 'date'
                    }}
                />
            </div>
        </div>
    )


}


/// displays a dictionary in a row of stylized headers and text
function StatRowDisplay({ input = [] }) {
    return <div className="columns is-multiline">
        {input.map(d => (
            <div className="column is-one-fifth-desktop">
                <div className="is-flex is-flex-direction-column is-justify-content-space-between p-3" style={{height: '115px'}}>
                    <div className="mb-1">{d.title}</div>
                    <div className={`${('' + d.value).length < 7 ? 'is-size-3' : 'is-size-5'} has-text-weight-semibold`}>
                        {d.value}
                    </div>
                </div>
            </div>
        ))}
    </div>
}

function HighLevelStats({ projects, users, dashboardStats, subdomain }) {

    let stats_to_display =  Object.keys(dashboardStats)
        .filter(k => k !== 'new_users')
        .map(k => ({
            title: startCase(k),
            value:  k.includes('value') ? formatters.USDCurrency.formatter(dashboardStats[k]) : dashboardStats[k],
            // apply this formatter only if 'value' is in the key somewhere
        }))


    if (subdomain.length) {
        stats_to_display = [
            {
                title: 'Active Users',
                value: [...new Set(projects.map(d => d.user_id))].length
            },
            ...stats_to_display
        ]
    } else {
        stats_to_display = [
            {
                title: 'Active Users',
                value: users.length
            },
            {
                title: 'New Users',
                value: (dashboardStats?.new_users || []).length
            },
            ...stats_to_display
        ]
    }

    return (
        <div className="mx-4 p-4 has-background-white">
            <StatRowDisplay input={stats_to_display} />
        </div>
    )
}





function ProjectList({ projects = [], handleSearch }) {
    let [searchTerm, setSearchTerm] = useState('');
    let [typeFilter, setTypeFilter] = useState('ALL'); // Project type filter
    let [applicationsOnly, setApplicationsOnly] = useState(false);
    let [paidApplicationsOnly, setPaidApplicationsOnly] = useState(false);
    let [displayedProjects, setDisplayedProjects] = useState([]);
    let [bidDeskOnly, setBidDeskOnly] = useState(false);

    // sort and prep project data
    useEffect(() => {
      const filtered = projects
        .filter(d =>
          `${lowerCase(d.user_first_name)} ${lowerCase(d.user_last_name)}\t
           ${lowerCase(d.user_email)}\t
           ${lowerCase(d.data?.project_name || '')}\t
           ${lowerCase(d.data?.address?.formatted_address || '')}`
            .includes(lowerCase(searchTerm))
        )
        .filter(d => {
          if (typeFilter === 'ALL') return true;
          const t = (d?.type_of_project || '').toLowerCase();
          return t === typeFilter.toLowerCase();
        })
        .filter(d => (applicationsOnly ? d.application_id : true))
        .filter(d => (paidApplicationsOnly ? (d.application_payment_amount || 0) > 0 : true))
        .filter(d => bidDeskOnly? (d.is_biddesk || d.is_biddesk_application): true);

      setDisplayedProjects(filtered);
    }, [applicationsOnly, paidApplicationsOnly, projects, typeFilter, searchTerm, bidDeskOnly]);

    useEffect(() => {
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => handleSearch(searchTerm), 1000);
    }, [searchTerm]);

    return (
      <div>

        <div className="is-flex is-justify-content-flex-end is-align-items-center mb-5">
          <div className="field mr-6 mb-0">
            <div className="control">
              <div className="select is-medium">
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                  <option value="ALL">All</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Residential">Residential</option>
                </select>
              </div>
            </div>
          </div>

          <div
            className="button is-medium mr-6"
            onClick={() => setApplicationsOnly(!applicationsOnly)}
          >
            <span className="icon">
              {applicationsOnly ? (
                <i className="fas fa-check-square has-text-success" />
              ) : (
                <i className="far fa-square has-text-grey-light" />
              )}
            </span>
            <p className={applicationsOnly ? '' : 'has-text-grey-light'}>Applications Only</p>
          </div>

          <div
            className="button is-medium mr-6"
            onClick={() => setPaidApplicationsOnly(!paidApplicationsOnly)}
          >
            <span className="icon">
              {paidApplicationsOnly ? (
                <i className="fas fa-check-square has-text-success" />
              ) : (
                <i className="far fa-square has-text-grey-light" />
              )}
            </span>
            <p className={paidApplicationsOnly ? '' : 'has-text-grey-light'}>Paid Apps Only</p>
          </div>

          <div
            className="button is-medium mr-6"
            onClick={() => setBidDeskOnly(!bidDeskOnly)}
            >
            <span className="icon">
                {bidDeskOnly ? (
                <i className="fas fa-check-square has-text-success" />
                ) : (
                <i className="far fa-square has-text-grey-light" />
                )}
            </span>
            <p className={bidDeskOnly ? '' : 'has-text-grey-light'}>
                Bid Desk Only
            </p>
            </div>

          <div>
            <Input
              type="text"
              value={searchTerm}
              onChange={v => setSearchTerm(v)}
              inputClasses="is-medium"
              placeholder="Search"
            />
          </div>
        </div>

        {displayedProjects.map(project => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </div>
    );
  }



function SubdomainDashboard({ subdomain }) {
     return {

     }
}

function SiteDashboard(props) {
    let subdomain = getSubdomainString()

    let [startDate, setStartDate] = useState(DateTime.now().minus({ days: 30 }).toISODate())
    let [endDate, setEndDate] = useState(DateTime.now().toISODate())
    let [excludeAdmins, setExcludeAdmins] = useState(false)
    let [projects, setProjects] = useState([]);
    let [users, setUsers] = useState([])
    let [dashboardStats, setDashboardStats] = useState({});
    let [dashboardTimeseries, setDashboardTimeseries] = useState([]);
    let [currView, setCurrView] =  useState('projects')


    let projectSearchTimeout;

    useEffect(() => {
        toggleGlobalLoader(true);

        let calls = [
            pullProjectData(startDate, endDate, subdomain)
                .then(data => {
                    if (data) setProjects(data)
                })
                .finally(() => toggleGlobalLoader(false)),

            pullSiteStatsData(startDate, endDate, subdomain)
                .then(data => {
                    if (data) setDashboardStats(data)
                }),
            pullSiteTimeseriesData(startDate, endDate, subdomain)
                .then(data => {
                    if (data) setDashboardTimeseries(data)
                })

        ]

        if (!subdomain) {
            calls = [
                ...calls,
                pullUserData(startDate, endDate, subdomain)
                    .then(data => {
                        if (data) setUsers(data)
                    }),
            ]
        }

        Promise.allSettled([
            getAvailableImprovements(),
            ...calls,
        ]).then(() => toggleGlobalLoader(false));

    }, [startDate, endDate])

    // useEffect(() => {
    //     let admin_user_ids = users.filter(d => d.is_admin).map(d => d.user_id)
    //     setProjects(projects.filter(d => admin_user_ids.includes(d.user_id)));
    //     setUsers(projects.filter(d => admin_user_ids.includes(d.user_id)));
    //
    // }, [excludeAdmins])


    let handleProjectSearch = (searchTerm) => {
        let searchStartDate; let searchEndDate;
        if (!searchTerm) {
            searchStartDate = startDate;
            searchEndDate = endDate;
        }
        return pullProjectData(searchStartDate, searchEndDate, getSubdomainString(), searchTerm)
            .then(_d => {
                setProjects(_d)
            })
    }
    return (
        <AdminWrapper>
            <div className="m-6" style={{ maxWidth: '1600px' }}>
                <h3 className="is-size-2 has-text-primary">
                    Site Dashboard
                </h3>

                <div className="is-flex mt-4">
                    <div className="mr-3">
                        <Input
                            type="date"
                            label="Start Date"
                            inputClasses="is-medium"
                            value={startDate}
                            onChange={val => setStartDate(val)}
                        />
                    </div>
                    <div className="mr-3">
                        <Input
                            type="date"
                            label="End Date"
                            inputClasses="is-medium"
                            value={endDate}
                            onChange={val => setEndDate(val)}
                        />
                    </div>
                    {/* <div className={''}> */}
                    {/*    <Input */}
                    {/*        type={'checkbox'} */}
                    {/*        label={"Exclude Admins"} */}
                    {/*        inputClasses={"is-medium"} */}
                    {/*        value={excludeAdmins} */}
                    {/*        onChange={val => setExcludeAdmins(val)} */}
                    {/*    /> */}
                    {/* </div> */}
                </div>
                <div className="mt-6">
                    <div className="tabs  is-medium">
                        <ul>
                            <li className={currView === 'projects' && 'is-active'}>
                                <a onClick={() => setCurrView('projects')}>Projects</a>
                            </li>
                            <li className={currView === 'totals' && 'is-active'}>
                                <a onClick={() => setCurrView('totals')}>Totals</a>
                            </li>
                            {!subdomain &&
                                <>
                                    <li className={currView === 'users' && 'is-active'}>
                                        <a onClick={() => setCurrView('users')}>Users</a>
                                    </li>
                                    {/* <li className={currView === 'subdomains' && 'is-active'}>
                                        <a onClick={() => setCurrView('subdomains')}>Subdomains</a>
                                    </li> */}
                                </>
                            }

                        </ul>
                    </div>
                </div>
                {currView === 'totals' &&
                    <>
                        <div className="mt-6">
                            <HighLevelStats
                                dashboardStats={dashboardStats}
                                projects={projects}
                                users={users}
                                subdomain={subdomain}
                            />
                        </div>
                        <div className="mt-6">
                            <div>{dashboardTimeseries[0]?.date}</div>
                            <TimeseriesChart data={dashboardTimeseries} />
                        </div>
                    </>
                }

                {currView === 'users' &&
                    <div className="mt-6">
                        <TopUserTable projects={projects} users={users} dashboardStats={dashboardStats} />
                    </div>
                }

                {currView === 'projects' &&
                    <div className="mt-6">
                        <ProjectList projects={projects} handleSearch={searchTerm => handleProjectSearch(searchTerm)} />
                    </div>
                }
            </div>
        </AdminWrapper>
    )
}

export default SiteDashboard